import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/api/client";

export type AdminUser = {
  id: string;
  email: string;
  display_name: string;
  role: "user" | "admin" | "superadmin";
  status: "active" | "disabled" | "pending";
  created_at: string;
  last_login_at: string | null;
  allow_proxy_tui?: boolean;
  /** The user's latest accepted Terms version and when, or null when
   *  never accepted (absent from a backend older than the gate). */
  terms_version?: number | null;
  terms_accepted_at?: string | null;
  /** The same for the Privacy Policy. */
  privacy_version?: number | null;
  privacy_accepted_at?: string | null;
  /** A removed account (Terms 19.3): shown as "Deleted user", no actions. */
  deleted?: boolean;
  /** The organisations the user belongs to, by name, with their role in
   *  each. Only those the viewer may see: every live org for a platform
   *  admin, the viewer's own orgs for an org admin. Absent from the
   *  single-row replies of the role and flag changes. */
  orgs?: AdminUserOrg[];
};

export type AdminUserOrg = {
  id: string;
  slug: string;
  name: string;
  role: "member" | "manager" | "admin";
};

export type AdminUserStats = {
  sessions: number;
  documents: number;
  documents_intent: number;
  reviews: number;
  summaries: number;
  draft_prs: number;
  draft_issues: number;
  tokens_30d: number;
  /** Credit micros the user's turns were charged in the last 30 days
   *  (their usage debits). Absent when the viewer may not see credits. */
  credits_used_micros_30d?: number;
  /** Raw API cost of the same turns, USD micros. Absent unless the
   *  viewer may see dollars. */
  cost_usd_micros_30d?: number;
};

export type AdminAuditEvent = {
  id: number;
  occurred_at: string;
  actor_user_id: string | null;
  actor_kind: string;
  action: string;
  resource_kind: string | null;
  resource_id: string | null;
  /** The tenant the event belongs to; null for platform-level events
   *  (a login, a cron tick, a catalogue edit). */
  org_id: string | null;
  metadata: Record<string, unknown>;
};

export function useAdminUsers() {
  return useQuery({
    queryKey: ["admin", "users"],
    queryFn: async () => (await api.get<AdminUser[]>("/admin/users")).data,
    staleTime: 30_000,
  });
}

export function useAdminUserStats() {
  return useQuery({
    queryKey: ["admin", "users", "stats"],
    queryFn: async () =>
      (await api.get<Record<string, AdminUserStats>>("/admin/users/stats"))
        .data,
    staleTime: 30_000,
  });
}

export function useSetUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      role,
    }: {
      userId: string;
      role: "user" | "admin" | "superadmin";
    }) =>
      (await api.post<AdminUser>(`/admin/users/${userId}/role`, { role })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

/** Remove a user's account as a platform admin (Terms 19.3): the same
 *  anonymisation and deletion as the user's own "Delete my account". The
 *  backend 409s ``{code: "sole_admin", orgs}`` while the user is the only
 *  admin of an organisation. */
export function useRemoveUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: string) => {
      await api.delete(`/admin/users/${userId}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export function useSetAllowProxyTui() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      userId,
      allow,
    }: {
      userId: string;
      allow: boolean;
    }) =>
      (
        await api.post<AdminUser>(`/admin/users/${userId}/allow-proxy-tui`, {
          allow,
        })
      ).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "users"] }),
  });
}

export type AdminAuditFilters = {
  actionPrefix?: string;
  actorUserId?: string;
  beforeId?: number;
  limit?: number;
};

export type GitlabImpersonationCheck = {
  status: "ok" | "warning" | "error";
  effective_mode: string;
  configured_mode: string;
  scopes: string[];
  is_admin: boolean;
  username: string | null;
  detail: string | null;
};

export function useGitlabImpersonationCheck() {
  return useQuery({
    queryKey: ["admin", "gitlab-impersonation-check"],
    queryFn: async () =>
      (
        await api.get<GitlabImpersonationCheck>(
          "/admin/gitlab-impersonation-check",
        )
      ).data,
    staleTime: 60_000,
  });
}

export function useAdminAudit(filters: AdminAuditFilters = {}) {
  const { actionPrefix = "", actorUserId = "", beforeId, limit = 50 } = filters;
  const params = new URLSearchParams();
  if (actionPrefix) params.set("action_prefix", actionPrefix);
  if (actorUserId) params.set("actor_user_id", actorUserId);
  if (beforeId) params.set("before_id", String(beforeId));
  params.set("limit", String(limit));
  const qs = params.toString();
  return useQuery({
    queryKey: [
      "admin",
      "audit",
      actionPrefix,
      actorUserId,
      beforeId ?? 0,
      limit,
    ],
    queryFn: async () =>
      (await api.get<AdminAuditEvent[]>(`/admin/audit?${qs}`)).data,
    // Only auto-refresh the live tail (no cursor). Paged history
    // is static. 30 s on the live tail — admin audit events trickle
    // in at human pace, no need to poll twice a minute.
    refetchInterval: beforeId ? false : 30_000,
    placeholderData: keepPreviousData,
  });
}
